import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { boardsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, LayoutGrid, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const BOARD_TYPE_LABELS = {
  gst_return: 'GST', income_tax: 'IT', tds_return: 'TDS',
  audit: 'Audit', roc_compliance: 'ROC', advisory: 'Advisory', other: 'Other'
};

const BOARD_TYPE_COLORS = {
  gst_return: 'bg-emerald-100 text-emerald-700',
  income_tax: 'bg-blue-100 text-blue-700',
  tds_return: 'bg-violet-100 text-violet-700',
  audit: 'bg-amber-100 text-amber-700',
  roc_compliance: 'bg-rose-100 text-rose-700',
  advisory: 'bg-cyan-100 text-cyan-700',
  other: 'bg-gray-100 text-gray-700',
};

const BoardsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);
  const [newBoard, setNewBoard] = useState({ name: '', description: '', board_type: 'other' });
  const [editBoard, setEditBoard] = useState(null);
  const [editBoardName, setEditBoardName] = useState('');
  const [editBoardDesc, setEditBoardDesc] = useState('');

  const isAdmin = user?.role === 'admin';

  const loadBoards = useCallback(async () => {
    try {
      const res = await boardsAPI.list();
      setBoards(res.data);
    } catch (err) { logger.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadBoards(); }, [loadBoards]);

  const handleCreateBoard = async (e) => {
    e.preventDefault();
    try {
      await boardsAPI.create(newBoard);
      toast.success('Board created');
      setBoardDialogOpen(false);
      setNewBoard({ name: '', description: '', board_type: 'other' });
      loadBoards();
    } catch (err) { toast.error('Failed to create board'); }
  };

  const handleDeleteBoard = async (e, boardId, boardName) => {
    e.stopPropagation();
    if (!window.confirm(`Delete board "${boardName}" and all its tasks? This cannot be undone.`)) return;
    try {
      await boardsAPI.delete(boardId);
      toast.success(`Board "${boardName}" deleted`);
      loadBoards();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete board'); }
  };

  const handleEditBoard = (e, board) => {
    e.stopPropagation();
    setEditBoard(board);
    setEditBoardName(board.name);
    setEditBoardDesc(board.description || '');
  };

  const handleSaveBoard = async () => {
    if (!editBoardName.trim()) return;
    try {
      await boardsAPI.update(editBoard.id, { name: editBoardName.trim(), description: editBoardDesc.trim() });
      toast.success('Board updated');
      setEditBoard(null);
      loadBoards();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update board'); }
  };

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          <LayoutGrid className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{boards.length} board{boards.length !== 1 ? 's' : ''}</span>
        </div>
        {isAdmin && (
          <Dialog open={boardDialogOpen} onOpenChange={setBoardDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="add-board-button">
                <Plus className="h-4 w-4 mr-1" /> New Board
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Board</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateBoard} className="space-y-4">
                <div>
                  <Label>Board Name *</Label>
                  <Input value={newBoard.name} onChange={e => setNewBoard({...newBoard, name: e.target.value})} required data-testid="board-name-input" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={newBoard.description} onChange={e => setNewBoard({...newBoard, description: e.target.value})} />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={newBoard.board_type} onValueChange={v => setNewBoard({...newBoard, board_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['gst_return','income_tax','tds_return','audit','roc_compliance','advisory','other'].map(t => (
                        <SelectItem key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full" data-testid="board-create-submit">Create Board</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Board Tiles Grid */}
      {boards.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {boards.map(b => {
            const typeColor = BOARD_TYPE_COLORS[b.board_type] || BOARD_TYPE_COLORS.other;
            return (
              <button
                key={b.id}
                onClick={() => navigate(`/dashboard/boards/${b.id}`)}
                className="relative flex flex-col items-start p-4 rounded-xl border-2 text-left transition-all duration-200 border-border bg-card hover:border-muted-foreground/30 hover:shadow-[var(--shadow-sm)]"
                data-testid={`board-tab-${b.id}`}
              >
                <div className="flex items-center justify-between w-full mb-1.5">
                  <Badge variant="secondary" className={`text-[10px] px-1.5 py-0.5 ${typeColor}`}>
                    {BOARD_TYPE_LABELS[b.board_type] || b.board_type}
                  </Badge>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <span
                        role="button"
                        onClick={(e) => handleEditBoard(e, b)}
                        className="text-muted-foreground hover:text-primary transition-colors p-0.5 rounded"
                        title="Edit board"
                        data-testid={`edit-board-${b.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </span>
                      <span
                        role="button"
                        onClick={(e) => handleDeleteBoard(e, b.id, b.name)}
                        className="text-muted-foreground hover:text-destructive transition-colors p-0.5 rounded"
                        title="Delete board"
                        data-testid={`delete-board-${b.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-sm font-semibold truncate w-full">{b.name}</p>
                {b.description && (
                  <p className="text-xs text-muted-foreground truncate w-full mt-0.5">{b.description}</p>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <LayoutGrid className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold mb-1">{isAdmin ? 'No boards yet' : 'No boards available'}</h3>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? 'Create your first board to start managing tasks.' : 'No task boards have been created yet.'}
          </p>
        </div>
      )}

      {/* Edit Board Dialog */}
      <Dialog open={!!editBoard} onOpenChange={(open) => !open && setEditBoard(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Board</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Board Name</Label>
              <Input
                value={editBoardName}
                onChange={(e) => setEditBoardName(e.target.value)}
                placeholder="Board name"
                data-testid="edit-board-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={editBoardDesc}
                onChange={(e) => setEditBoardDesc(e.target.value)}
                placeholder="Description (optional)"
                data-testid="edit-board-desc-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditBoard(null)}>Cancel</Button>
              <Button onClick={handleSaveBoard} data-testid="save-board-button">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardsPage;
